import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stethoscope, LogOut, FileText, AlertTriangle, Send, Loader2, Plus, Trash2, Upload, Pill, CalendarHeart, Download, UserRound, History as HistoryIcon, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { openPdf } from '../lib/downloads';
import { t } from '../i18n';

const statusBadge = (lang) => ({
  pending: { bg: '#FEF3C7', fg: '#A16207', label: t(lang, 'doctor_pending') },
  reviewed: { bg: '#E8F5E9', fg: '#2A5244', label: t(lang, 'doctor_reviewed') },
  closed: { bg: '#E0E7FF', fg: '#3730A3', label: t(lang, 'doctor_closed') },
});

const SEV_BADGE = {
  low: { bg: '#E8F5E9', fg: '#2A5244' },
  moderate: { bg: '#FDECE4', fg: '#E07A5F' },
  high: { bg: '#FEE2E2', fg: '#D9534F' },
  emergency: { bg: '#FEE2E2', fg: '#B00020' },
};

export default function DoctorDashboard() {
  const nav = useNavigate();
  const lang = localStorage.getItem('hb_lang') || 'en';
  const STATUS_BADGE = statusBadge(lang);
  const [doctor, setDoctor] = useState(null);
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [text, setText] = useState('');
  const [presc, setPresc] = useState([{ medicine: '', dosage: '', duration: '', notes: '' }]);
  const [followUp, setFollowUp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [severityFilter, setSeverityFilter] = useState('all');

  useEffect(() => {
    const t = localStorage.getItem('hb_token');
    const d = localStorage.getItem('hb_doctor');
    if (!t || !d) { nav('/doctor/login'); return; }
    setDoctor(JSON.parse(d));
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/doctor/referrals');
      setRefs(data.referrals);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.removeItem('hb_token'); nav('/doctor/login'); return; }
      toast.error(t(lang, 'could_not_load_referrals'));
    } finally { setLoading(false); }
  };

  const logout = () => {
    localStorage.removeItem('hb_token');
    localStorage.removeItem('hb_refresh_token');
    localStorage.removeItem('hb_doctor');
    nav('/doctor/login');
  };

  const openReferral = (r) => {
    setActive(r);
    setText(r.response?.text || '');
    setPresc(r.response?.prescriptions?.length ? r.response.prescriptions : [{ medicine: '', dosage: '', duration: '', notes: '' }]);
    setFollowUp(r.response?.follow_up_date || '');
  };

  const submit = async () => {
    if (!text.trim()) { toast.error(t(lang, 'please_write_response')); return; }
    setSubmitting(true);
    try {
      const cleanPresc = presc.filter(p => p.medicine.trim()).map(p => ({
        medicine: p.medicine.trim(), dosage: p.dosage.trim(),
        duration: p.duration.trim(), notes: p.notes?.trim() || '',
      }));
      await api.post('/doctor/referrals/respond', {
        referral_id: active.id, text, prescriptions: cleanPresc,
        follow_up_date: followUp || null,
      });
      toast.success(t(lang, 'response_sent_patient'));
      setActive(null);
      load();
    } catch (e) { toast.error(t(lang, 'could_not_send_response')); }
    finally { setSubmitting(false); }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post(`/doctor/referrals/${active.id}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t(lang, 'signed_pdf_uploaded'));
      setActive({ ...active, response: { ...(active.response || {}), signed_pdf_url: data.signed_pdf_url }});
      load();
    } catch (e) { toast.error(t(lang, 'upload_failed')); }
    finally { setUploading(false); }
  };

  const counts = refs.reduce((a, r) => { a[r.status] = (a[r.status]||0) + 1; return a; }, { pending: 0, reviewed: 0, closed: 0 });
  const severityRank = { emergency: 0, high: 1, moderate: 2, low: 3 };
  const visibleRefs = refs
    .filter((r) => statusFilter === 'all' || r.status === statusFilter)
    .filter((r) => severityFilter === 'all' || r.report?.severity === severityFilter)
    .sort((a, b) => (severityRank[a.report?.severity || 'low'] ?? 9) - (severityRank[b.report?.severity || 'low'] ?? 9));
  const activePatient = active?.report?.patient || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 fade-up">
      {/* Doctor header */}
      <Card className="p-6 sm:p-7 rounded-3xl bg-[#1E3F33] text-white border-0 hb-shadow-lg flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-[#E07A5F] grid place-items-center"><Stethoscope className="h-7 w-7"/></div>
          <div>
            <div className="font-display text-2xl">{doctor?.name}</div>
            {doctor?.verification_status !== 'verified' && (
              <div className="mt-2 inline-flex rounded-full bg-[#FEF3C7] px-3 py-1 text-xs font-semibold text-[#7A5A00]">
                Verification pending
              </div>
            )}
            <div className="text-sm text-white/75">{doctor?.specialty} · {doctor?.city}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs text-white/70">{t(lang, 'pending_reviewed')}</div>
            <div className="font-semibold text-lg">{counts.pending} / {counts.reviewed}</div>
          </div>
          <Button variant="outline" onClick={logout} className="rounded-full bg-transparent border-white/30 text-white hover:bg-white/10" data-testid="doctor-logout-btn">
            <LogOut className="h-4 w-4 mr-2"/> {t(lang,'nav_logout')}
          </Button>
        </div>
      </Card>

      {/* Referrals */}
      <h2 className="font-display text-3xl text-[#1A1A1A] mt-10">{t(lang,'patient_referrals')}</h2>
      <p className="text-[#4A4A4A] mt-1">{t(lang,'review_reports_guidance')}</p>

      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="h-auto rounded-full bg-[#F4F1EA] p-1">
            <TabsTrigger value="pending" className="rounded-full px-4 py-2">Pending ({counts.pending || 0})</TabsTrigger>
            <TabsTrigger value="reviewed" className="rounded-full px-4 py-2">Reviewed ({counts.reviewed || 0})</TabsTrigger>
            <TabsTrigger value="closed" className="rounded-full px-4 py-2">Closed ({counts.closed || 0})</TabsTrigger>
            <TabsTrigger value="all" className="rounded-full px-4 py-2">All ({refs.length})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-[#7A7A7A]" />
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="h-11 w-44 rounded-full border-[#E2E8F0]"><SelectValue placeholder="Severity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severity</SelectItem>
              <SelectItem value="emergency">Emergency</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="moderate">Moderate</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-[#7A7A7A] py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2"/>{t(lang,'loading')}</div>
      ) : visibleRefs.length === 0 ? (
        <div className="text-center text-[#7A7A7A] py-12">No referrals match this filter.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5 mt-6" data-testid="doctor-referrals-list">
          {visibleRefs.map(r => {
            const s = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
            const sv = SEV_BADGE[r.report?.severity || 'low'];
            return (
              <Card key={r.id} className="p-6 rounded-3xl border border-[#E8E1D5] bg-white hover:-translate-y-1 transition-all hb-shadow-soft cursor-pointer"
                onClick={()=>openReferral(r)} data-testid={`doctor-referral-card-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-xl text-[#1A1A1A]">{r.patient_name}</div>
                    <div className="text-sm text-[#7A7A7A]">{r.patient_phone} · {new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <Badge className="rounded-full font-semibold border" style={{ background: s.bg, color: s.fg, borderColor: s.fg+'33' }}>{s.label}</Badge>
                    {r.report?.severity && <Badge className="rounded-full font-semibold border" style={{ background: sv.bg, color: sv.fg, borderColor: sv.fg+'33' }}>{r.report.severity}</Badge>}
                  </div>
                </div>
                <p className="text-[15px] text-[#1A1A1A] mt-4 line-clamp-3">{r.report?.summary}</p>
                {r.patient_message && (
                  <div className="mt-3 p-3 rounded-xl bg-[#F4F1EA] text-sm">
                    <span className="text-xs uppercase tracking-wide text-[#7A7A7A]">{t(lang,'patient_note')} · </span>{r.patient_message}
                  </div>
                )}
                <div className="mt-4 flex justify-end">
                  <span className="text-[#1E3F33] font-semibold text-sm">{t(lang,'open_respond')} →</span>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!active} onOpenChange={(v)=>!v && setActive(null)}>
        <DialogContent className="rounded-3xl max-w-3xl max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{t(lang,'referral')} · {active.patient_name}</DialogTitle>
                <DialogDescription>{active.patient_phone} · {new Date(active.created_at).toLocaleString()}</DialogDescription>
              </DialogHeader>

              <div className="rounded-2xl border border-[#E8E1D5] bg-white p-5 space-y-4">
                <div className="flex items-center gap-2 text-[#1E3F33] font-semibold">
                  <UserRound className="h-5 w-5" /> Patient context
                </div>
                <div className="grid sm:grid-cols-4 gap-3 text-sm">
                  <div><div className="text-xs uppercase tracking-wide text-[#7A7A7A]">Age</div><div className="font-semibold">{activePatient.age || '-'}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-[#7A7A7A]">Gender</div><div className="font-semibold">{activePatient.gender || '-'}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-[#7A7A7A]">Language</div><div className="font-semibold">{(activePatient.language || 'en').toUpperCase()}</div></div>
                  <div><div className="text-xs uppercase tracking-wide text-[#7A7A7A]">Severity</div><div className="font-semibold">{active.report?.severity || '-'}</div></div>
                  <div className="sm:col-span-4"><div className="text-xs uppercase tracking-wide text-[#7A7A7A]">Medical history</div><div>{activePatient.medical_history || 'Not reported'}</div></div>
                </div>
                {active.previous_reports?.length > 0 && (
                  <div className="border-t border-[#E8E1D5] pt-4">
                    <div className="flex items-center gap-2 text-[#1E3F33] font-semibold text-sm mb-2"><HistoryIcon className="h-4 w-4" /> Previous summaries</div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {active.previous_reports.map((rep) => (
                        <button key={rep.id} type="button" onClick={() => openPdf(`/report/${rep.id}/pdf`, `health-buddy-${rep.id}.pdf`)} className="text-left rounded-xl bg-[#F4F1EA] p-3 text-sm hover:bg-[#E8E1D5]">
                          <div className="font-semibold text-[#1A1A1A]">{new Date(rep.created_at).toLocaleDateString()} · {rep.severity}</div>
                          <div className="text-[#4A4A4A] line-clamp-2">{rep.summary}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Report summary */}
              <div className="rounded-2xl bg-[#F4F1EA] p-5 space-y-3">
                <div className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#1E3F33]"/><b>{t(lang,'health_buddy_report')}</b>
                  {active.report?.severity && <Badge className="rounded-full ml-auto" style={{ background: SEV_BADGE[active.report.severity].bg, color: SEV_BADGE[active.report.severity].fg }}>{active.report.severity}</Badge>}
                </div>
                <div className="text-[15px] text-[#1A1A1A]">{active.report?.summary}</div>
                {active.report?.likely_conditions?.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-[#7A7A7A] mb-1">{t(lang,'likely_conditions')}</div>
                    <ul className="list-disc pl-5 text-sm">
                      {active.report.likely_conditions.map((c,i)=><li key={i}><b>{c.name}</b> ({c.probability}) — {c.explanation}</li>)}
                    </ul>
                  </div>
                )}
                {active.report?.red_flags?.length > 0 && (
                  <div className="text-[#D9534F] text-sm flex items-start gap-2"><AlertTriangle className="h-4 w-4 mt-0.5"/><span><b>{t(lang,'red_flags_label')}:</b> {active.report.red_flags.join(', ')}</span></div>
                )}
                <Button onClick={() => openPdf(`/report/${active.report_id}/pdf`, `health-buddy-${active.report_id}.pdf`)} size="sm" variant="outline" className="rounded-full border-[#1E3F33] text-[#1E3F33]"><Download className="h-3.5 w-3.5 mr-1.5"/>{t(lang,'download_ai_report_pdf')}</Button>
              </div>

              {active.patient_message && (
                <div className="rounded-2xl bg-white border border-[#E8E1D5] p-4 mt-3">
                  <div className="text-xs uppercase tracking-wide text-[#7A7A7A] mb-1">{t(lang,'patient_message')}</div>
                  <div className="text-[15px]">{active.patient_message}</div>
                </div>
              )}

              {/* Doctor response form */}
              <div className="mt-4 space-y-4">
                <div>
                  <Label className="font-semibold text-[#1E3F33]">{t(lang,'your_advice_diagnosis')}</Label>
                  <Textarea value={text} onChange={(e)=>setText(e.target.value)} placeholder={t(lang,'write_clear_response')}
                    className="mt-1.5 min-h-28 rounded-xl border-[#E2E8F0]" data-testid="doctor-response-text"/>
                </div>

                <div>
                  <Label className="font-semibold text-[#1E3F33] flex items-center gap-2"><Pill className="h-4 w-4"/>{t(lang,'prescription')}</Label>
                  <div className="space-y-2 mt-1.5">
                    {presc.map((p, i) => (
                      <div key={i} className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_1fr_1.2fr_auto] gap-2 items-center">
                        <Input value={p.medicine} placeholder={t(lang,'medicine')} onChange={(e)=>{const c=[...presc];c[i].medicine=e.target.value;setPresc(c);}} className="rounded-xl" data-testid={`presc-med-${i}`}/>
                        <Input value={p.dosage} placeholder={t(lang,'dosage_placeholder')} onChange={(e)=>{const c=[...presc];c[i].dosage=e.target.value;setPresc(c);}} className="rounded-xl"/>
                        <Input value={p.duration} placeholder={t(lang,'duration_placeholder')} onChange={(e)=>{const c=[...presc];c[i].duration=e.target.value;setPresc(c);}} className="rounded-xl"/>
                        <Input value={p.notes} placeholder={t(lang,'notes_placeholder')} onChange={(e)=>{const c=[...presc];c[i].notes=e.target.value;setPresc(c);}} className="rounded-xl"/>
                        <Button size="icon" variant="ghost" onClick={()=>setPresc(presc.length>1?presc.filter((_,idx)=>idx!==i):presc)} className="rounded-full">
                          <Trash2 className="h-4 w-4"/>
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={()=>setPresc([...presc, {medicine:'',dosage:'',duration:'',notes:''}])} className="rounded-full border-[#1E3F33] text-[#1E3F33]" data-testid="presc-add-btn">
                      <Plus className="h-3.5 w-3.5 mr-1.5"/>{t(lang,'add_medicine')}
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="font-semibold text-[#1E3F33] flex items-center gap-2"><CalendarHeart className="h-4 w-4"/>{t(lang,'followup_optional')}</Label>
                  <Input type="date" value={followUp} onChange={(e)=>setFollowUp(e.target.value)} className="mt-1.5 h-11 rounded-xl border-[#E2E8F0] sm:max-w-xs" data-testid="doctor-followup-date"/>
                </div>

                <div>
                  <Label className="font-semibold text-[#1E3F33] flex items-center gap-2"><Upload className="h-4 w-4"/>{t(lang,'signed_prescription_optional')}</Label>
                  <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                    <input type="file" accept="application/pdf" onChange={(e)=>uploadPdf(e.target.files?.[0])} className="block text-sm" data-testid="doctor-upload-pdf"/>
                    {uploading && <Loader2 className="h-4 w-4 animate-spin"/>}
                    {active.response?.signed_pdf_url && (
                      <button type="button" onClick={() => openPdf(active.response.signed_pdf_url, `signed-prescription-${active.id}.pdf`)} className="text-sm text-[#1E3F33] underline">{t(lang,'view_uploaded')}</button>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="ghost" onClick={()=>setActive(null)} className="rounded-full">{t(lang,'close')}</Button>
                  <Button onClick={submit} disabled={submitting}
                    className="rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white font-semibold"
                    data-testid="doctor-submit-response">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <Send className="h-4 w-4 mr-2"/>}
                    {t(lang,'send_to_patient')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
